import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";

const KINDS = [
  ["pitch", "#0ea5e9"],
  ["tax", "#64748b"],
  ["rental", "#f59e0b"],
  ["shop", "#10b981"],
  ["other", "#cbd5e1"],
];
const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

export default function AdminRevenue() {
  const { t } = useLang();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rep, setRep] = useState(null);

  useEffect(() => { api.get(`/revenue?year=${year}`).then(({ data }) => setRep(data)).catch(() => setRep(null)); }, [year]);

  const max = Math.max(1, ...((rep?.months || []).map((m) => m.total)));

  return (
    <Panel
      title={`${t("admin_revenue")} ${year}`}
      testid="revenue-panel"
      action={
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setYear(year - 1)} data-testid="revenue-prev-year">‹</Button>
          <b className="tabular-nums text-sm" data-testid="revenue-year">{year}</b>
          <Button variant="ghost" size="sm" onClick={() => setYear(year + 1)} data-testid="revenue-next-year">›</Button>
        </div>
      }
    >
      {!rep ? <Empty /> : (
        <div className="p-5 space-y-5">
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[...KINDS.map(([k, c]) => [k, c, rep.totals[k]]), ["total", "#0f172a", rep.totals.total]].map(([k, color, val]) => (
              <div key={k} className="rounded-xl border border-border bg-card p-3" data-testid={`revenue-kpi-${k}`}>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{t(`rev_${k}`)}</p>
                <p className="font-display font-extrabold text-lg tabular-nums" style={{ color }}>{money(val)}</p>
              </div>
            ))}
          </div>

          {/* Gestapelde balken per maand */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-end gap-2" style={{ height: 176 }}>
              {rep.months.map((m, i) => (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${MONTHS[i]} · ${money(m.total)}`} data-testid={`revenue-bar-${m.month}`}>
                  <span className="text-[9px] tabular-nums text-muted-foreground">{m.total ? Math.round(m.total) : ""}</span>
                  <div className="w-full flex flex-col-reverse rounded-t overflow-hidden" style={{ height: Math.max(3, (m.total / max) * 130) }}>
                    {KINDS.map(([k, c]) => m[k] > 0 && (
                      <div key={k} style={{ height: `${(m[k] / m.total) * 100}%`, background: c }} />
                    ))}
                    {!m.total && <div className="h-full bg-slate-100" />}
                  </div>
                  <span className="text-[10px] font-bold text-muted-foreground">{MONTHS[i]}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 pt-3 text-[11px] text-muted-foreground">
              {KINDS.map(([k, c]) => (
                <span key={k} className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} />{t(`rev_${k}`)}</span>
              ))}
            </div>
          </div>

          <div className="overflow-auto cb-scroll">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-muted/40 text-muted-foreground">
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("month")}</th>
                  {KINDS.map(([k]) => <th key={k} className="px-3 py-2 text-right font-bold uppercase tracking-wider text-[10px]">{t(`rev_${k}`)}</th>)}
                  <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-[10px]">{t("total")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rep.months.map((m, i) => (
                  <tr key={m.month} data-testid={`revenue-row-${m.month}`} className={m.total ? "" : "text-slate-300"}>
                    <td className="px-3 py-2 font-bold text-foreground/90">{MONTHS[i]}</td>
                    {KINDS.map(([k]) => <td key={k} className="px-3 py-2 text-right tabular-nums">{m[k] ? money(m[k]) : "·"}</td>)}
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-primary">{m.total ? money(m.total) : "·"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />{t("admin_revenue_desc")}
          </p>
        </div>
      )}
    </Panel>
  );
}
